<template lang="pug">
.prize-purse-breakdown(v-if="amount")
  .tabs
    div(v-for="tab in tabs" :class="{ 'selected': tab === selectedTab }" @click="selectedTab = tab")
      span {{ tab }}
  prize-purse-table(:rows="breakdown" v-if="amount" :key="selectedTab")
</template>
<script lang="ts" setup>
import { ref, computed, onMounted } from 'vue'
import PrizePurseTable from '@/js/components/events/prize-purse-table.vue'
import { useRogue } from '@/js/services/Rogue'
import { INVITATIONAL_BREAKDOWN, STRONGMAN_ADDER_PCT, STRONGMAN_SPLIT_BREAKDOWN, STRONGMAN_BREAKDOWN } from './utils'

const props = defineProps<{eventId: string}>()

const selectedTab = ref('Individual')

const amount = ref<number>(0)
const strongmanSidepurse = ref<number>(0)
const tabs = computed(() => (strongmanSidepurse.value ? ['Individual', 'Strongman', 'Strongwoman'] : ['Individual', 'Strongman']))

const rogue = useRogue()

const breakdown = computed<{place: number, percent?: number, payout: string}[]>(() => {
  let pool = amount.value
  // The STRONGMAN_BREAKDOWN table is the values if the strong competition is NOT split with strongwoman.
  let rows = selectedTab.value === 'Individual' ? INVITATIONAL_BREAKDOWN : STRONGMAN_BREAKDOWN

  // Way to grant additional flat cash to specific entries
  let sidePurse: number[] = []

  if (strongmanSidepurse.value) {
  // In 2024, the "total" includes a 215k side purse that will be split just for strongman / strongwoman competitors
  // This is because Strongman percentages of main purse were halved to add strongwoman.

    // The side purse is not split using the normal percentages, so we need to pull the side purse out of the main calculator
    pool -= strongmanSidepurse.value
    if (selectedTab.value === 'Strongman' || selectedTab.value === 'Strongwoman') {
      // The strongman and strongwoman competitors have to use the smaller splits - but they get an additional adder from the 215k side purse
      rows = STRONGMAN_SPLIT_BREAKDOWN
      sidePurse = STRONGMAN_ADDER_PCT.map(sidePursePct => strongmanSidepurse.value * (sidePursePct / 100))
    }
  }

  return rows
    .map((pct, place) => ({
      place   : place + 1,
      percent : strongmanSidepurse.value ? undefined : pct,
      value   : ((pct / 100) * pool) + (sidePurse[place] || 0),
      payout  : rogue.currency(((pct / 100) * pool) + (sidePurse[place] || 0), { currency: 'USD' }),
    }))
})

onMounted(() => rogue.fetch<{total: number, bitcoin: number, amount: number, strongmanSidepurse?: number}>(`/svc_prizepurse/${props.eventId}`).then(resp => {
  amount.value = resp.total
  strongmanSidepurse.value = resp.strongmanSidepurse || 0
}))

</script>
<style lang="scss" scoped>
.prize-purse-breakdown {
  .tabs {
    display: flex;

    @include mobile() {
      display: block;
      width: 100%;
    }

    div {
      font-weight: $weight-bold;
      font-family: $font-header;
      font-size: $size-l;
      color: $color-white;
      background: $color-gray-dark;
      text-transform: uppercase;
      padding: 20px;
      text-align: center;
      width: 50%;
      cursor: pointer;

      &:not(:last-child){
        border-right: 1px solid #333
      }

      @include mobile() {
        width: 100%;
        border-right: none
      }

      &.selected {
        background: $color-black
      }
    }
  }
}
</style>
<style lang="scss" >
.prize-purse-table {
  @include mobile() {
    table:last-child {
      th {
        visibility: collapse;
        padding: 0 8px;

        div {
          height: 0;
        }
      }
    }
  }
}</style>
